/**
 * 知识抽查系统 - 首页
 * 展示本周学习和历史学习时间轴
 */
import React, { useState, useEffect } from 'react';
import { Card, Button, Tag, message, Empty, Timeline, Popconfirm } from 'antd';
import { useNavigate } from 'react-router-dom';
import { BookOutlined, CalendarOutlined, SettingOutlined, EyeOutlined, BarChartOutlined, LogoutOutlined, UserOutlined, UnorderedListOutlined, LineChartOutlined } from '@ant-design/icons';
import knowledgeLotteryApi from '../services/knowledgeLotteryApi';
import { hasKnowledgeManagementPermission, hasKnowledgeManagementAccess, isSystemAdmin } from '../utils/knowledgeLotteryAuth';

const { Meta } = Card;

const KnowledgeLotteryHome = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [latestContent, setLatestContent] = useState(null);
  const [latestSessionId, setLatestSessionId] = useState(null); // 保存关联的会议ID
  const [historyContents, setHistoryContents] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [hasKnowledgePermission, setHasKnowledgePermission] = useState(false);
  const [hasManagementAccess, setHasManagementAccess] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // 格式化会议期数显示（如：2026年第3期会议）
  const formatSessionInfo = (content) => {
    if (content.sessionYear && content.sessionNumber) {
      return `${content.sessionYear}年第${content.sessionNumber}期会议`;
    }
    return null;
  };

  // 判断知识点是否已学习（优先使用后端返回的isLearned字段，否则根据学习日期判断）
  const isLearned = (content) => {
    // 优先使用后端返回的isLearned字段（基于会议状态）
    if (content.isLearned !== undefined) {
      return content.isLearned;
    }
    // 如果没有isLearned字段，使用原有的逻辑（兼容旧数据）
    if (!content.learnedDate) return false;
    const learnedDate = new Date(content.learnedDate);
    const now = new Date();
    // 只比较日期部分，忽略时间
    learnedDate.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    return learnedDate < now;
  };

  // 获取日期所在周的周一（周一开始）
  const getWeekStart = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay(); // 0 = 周日, 1 = 周一, ..., 6 = 周六
    const diff = day === 0 ? -6 : 1 - day; // 如果是周日，往前推6天；否则往前推到周一
    d.setDate(d.getDate() + diff);
    return d;
  };

  // 判断两个日期是否在同一周
  const isSameWeek = (date1, date2) => {
    const weekStart1 = getWeekStart(date1);
    const weekStart2 = getWeekStart(date2);
    return weekStart1.getTime() === weekStart2.getTime();
  };

  // 获取当前用户信息和权限
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
      } catch (e) {
        console.error('解析用户信息失败:', e);
      }
    }
    
    // 检查知识管理权限（用于显示管理后台按钮）
    const checkPermissions = async () => {
      const [viewPermission, managePermission, adminPermission] = await Promise.all([
        hasKnowledgeManagementPermission(), // 查看权限
        hasKnowledgeManagementAccess(), // 管理权限
        isSystemAdmin() // 系统管理员权限
      ]);
      setHasKnowledgePermission(viewPermission);
      setHasManagementAccess(managePermission);
      setIsAdmin(adminPermission);
      console.log('KnowledgeLotteryHome: 权限检查结果', { viewPermission, managePermission, adminPermission });
    };
    checkPermissions();
  }, []);

  // 退出登录
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    message.success('已退出登录');
    navigate('/login', { replace: true });
  };

  // 获取最新知识点和会议
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 获取所有知识点（用于历史学习）
      const contentsResponse = await knowledgeLotteryApi.knowledgeContentApi.getAllKnowledgeContents();
      
      // 获取会议列表（用于待学习知识点和时间轴展示）
      const sessionsResponse = await knowledgeLotteryApi.sessionApi.getAllSessions();
      
      // 处理知识点数据（用于历史学习）
      if (contentsResponse.success) {
        if (contentsResponse.data && contentsResponse.data.length > 0) {
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          
          // 筛选有学习日期的知识点
          const contentsWithDate = contentsResponse.data.filter(content => content.learnedDate);
          
          // 筛选"历史学习"：已学习的知识点（优先使用isLearned字段，否则根据学习日期判断）
          const learnedContents = contentsResponse.data
            .filter(content => {
              // 优先使用后端返回的isLearned字段（基于会议状态）
              if (content.isLearned !== undefined) {
                return content.isLearned;
              }
              // 如果没有isLearned字段，使用原有的逻辑（兼容旧数据）
              if (!content.learnedDate) return false;
              const learnedDate = new Date(content.learnedDate);
              learnedDate.setHours(0, 0, 0, 0);
              return learnedDate < now;
            })
            .sort((a, b) => {
              // 优先使用关联会议的日期，否则使用学习日期
              const dateA = a.associatedSession?.sessionDate 
                ? new Date(a.associatedSession.sessionDate)
                : (a.learnedDate ? new Date(a.learnedDate) : new Date(0));
              const dateB = b.associatedSession?.sessionDate 
                ? new Date(b.associatedSession.sessionDate)
                : (b.learnedDate ? new Date(b.learnedDate) : new Date(0));
              return dateB - dateA; // 降序，最新的在前
            });
          
          // 历史学习：已学习的知识点，按日期倒序
          setHistoryContents(learnedContents);
        } else {
          setHistoryContents([]);
        }
      }
      
      // 处理会议数据（用于待学习知识点）
      if (sessionsResponse.success) {
        if (sessionsResponse.data && sessionsResponse.data.length > 0) {
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          
          // 筛选待进行的会议（会议日期 >= 今天）
          const pendingSessions = sessionsResponse.data.filter(session => {
            if (!session.sessionDate) return false;
            const sessionDate = new Date(session.sessionDate);
            sessionDate.setHours(0, 0, 0, 0);
            // 待进行的会议：会议日期 >= 今天
            return sessionDate >= now;
          });
          
          console.log('待进行的会议:', pendingSessions.map(s => ({
            id: s.id,
            title: s.title,
            sessionDate: s.sessionDate,
            knowledgeContentIds: s.knowledgeContentIds?.length || 0,
            knowledgeContentId: s.knowledgeContentId ? 1 : 0
          })));
          
          // 按日期升序排序（最近的在前）
          const sortedPendingSessions = pendingSessions.sort((a, b) => {
            const dateA = new Date(a.sessionDate || a.createdAt || 0);
            const dateB = new Date(b.sessionDate || b.createdAt || 0);
            return dateA - dateB; // 升序，最近的在前
          });
          
          console.log('排序后的待进行会议:', sortedPendingSessions.map(s => ({
            id: s.id,
            title: s.title,
            sessionDate: s.sessionDate
          })));
          
          // 找到第一个有关联知识点的会议（支持多个知识点）
          const recentSession = sortedPendingSessions.find(s => {
            // 优先使用新字段：多个知识点
            if (s.knowledgeContentIds && s.knowledgeContentIds.length > 0) {
              return true;
            }
            // 兼容旧字段：单个知识点
            return s.knowledgeContentId;
          });
          
          console.log('找到的最近待开会议:', recentSession ? {
            id: recentSession.id,
            title: recentSession.title,
            sessionDate: recentSession.sessionDate,
            knowledgeContentIds: recentSession.knowledgeContentIds?.length || 0
          } : null);
          
          if (recentSession) {
            // 保存会议ID
            const sessionId = recentSession._id || recentSession.id;
            setLatestSessionId(sessionId);
            
            // 获取该会议关联的知识点ID（优先使用第一个知识点）
            let knowledgeId = null;
            if (recentSession.knowledgeContentIds && recentSession.knowledgeContentIds.length > 0) {
              // 新字段：多个知识点，取第一个
              const firstKnowledge = recentSession.knowledgeContentIds[0];
              knowledgeId = typeof firstKnowledge === 'object' 
                ? (firstKnowledge._id || firstKnowledge.id)
                : firstKnowledge;
            } else if (recentSession.knowledgeContentId) {
              // 兼容旧字段：单个知识点
              knowledgeId = typeof recentSession.knowledgeContentId === 'object' 
                ? (recentSession.knowledgeContentId._id || recentSession.knowledgeContentId.id)
                : recentSession.knowledgeContentId;
            }
            
            console.log('提取的知识点ID:', knowledgeId);
            
            if (knowledgeId) {
              // 从已获取的知识点列表中查找
              let associatedContent = null;
              if (contentsResponse.success && contentsResponse.data) {
                associatedContent = contentsResponse.data.find(content => {
                  const contentId = content._id || content.id;
                  return String(contentId) === String(knowledgeId);
                });
              }
              
              // 如果找到了关联的知识点，设置为待学习知识点
              if (associatedContent) {
                console.log('找到关联的知识点:', associatedContent.title);
                // 设置关联的会议信息，确保日期显示正确
                setLatestContent({ 
                  ...associatedContent, 
                  _isPendingLearning: true, 
                  _sessionId: sessionId,
                  associatedSession: {
                    sessionDate: recentSession.sessionDate,
                    title: recentSession.title,
                    _id: sessionId
                  }
                });
              } else {
                // 如果没有找到，尝试通过API获取
                try {
                  const detailResponse = await knowledgeLotteryApi.knowledgeContentApi.getKnowledgeContentById(knowledgeId);
                  if (detailResponse.success && detailResponse.data) {
                    // 设置关联的会议信息，确保日期显示正确
                    setLatestContent({ 
                      ...detailResponse.data, 
                      _isPendingLearning: true, 
                      _sessionId: sessionId,
                      associatedSession: {
                        sessionDate: recentSession.sessionDate,
                        title: recentSession.title,
                        _id: sessionId
                      }
                    });
                  } else {
                    setLatestContent(null);
                    setLatestSessionId(null);
                  }
                } catch (error) {
                  console.error('获取知识点详情失败:', error);
                  setLatestContent(null);
                  setLatestSessionId(null);
                }
              }
            } else {
              console.warn('会议没有关联的知识点');
              setLatestContent(null);
              setLatestSessionId(null);
            }
          } else {
            // 没有找到有关联知识点的待进行会议
            console.log('没有找到待进行的会议或没有关联知识点');
            setLatestContent(null);
            setLatestSessionId(null);
          }
        } else {
          setLatestContent(null);
          setLatestSessionId(null);
        }
      } else {
        // API 返回 success: false，说明是真正的错误
        message.error(sessionsResponse.message || '获取会议列表失败');
        setLatestContent(null);
        setLatestSessionId(null);
      }
    } catch (error) {
      console.error('获取数据失败:', error);
      // 只有在网络错误或异常时才显示错误
      message.error(error.message || '获取数据失败，请检查网络连接');
    }
    setLoading(false);
  };

  const handleViewDetail = (contentId, isPendingLearning = false, sessionId = null) => {
    // 如果是待学习知识点且有会议ID，跳转到会议详情页面（可以进行抽签）
    if (isPendingLearning && sessionId) {
      const id = String(sessionId);
      navigate(`/knowledge-lottery/session/${id}`);
      return;
    }
    
    // 否则跳转到知识点详情
    const id = String(contentId);
    navigate(`/knowledge-lottery/knowledge/${id}`);
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}年${month}月${day}日`;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {/* Header */}
      <header style={{ 
        borderBottom: '4px solid #ff4d4f', 
        background: '#fff',
        padding: '24px 0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ fontSize: '32px', fontWeight: 'bold', margin: 0 }}>
                应知应会知识抽查
              </h1>
              <p style={{ color: '#666', marginTop: '8px', fontFamily: 'monospace', fontSize: '14px' }}>
                部门周例会 / 随机抽查系统
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Button 
                icon={<BookOutlined />}
                onClick={() => navigate('/knowledge-lottery/knowledge-board')}
                style={{ border: '2px solid #ff4d4f', fontWeight: 'bold' }}
              >
                知识看板
              </Button>
              <Button 
                icon={<BarChartOutlined />}
                onClick={() => navigate('/knowledge-lottery/statistics')}
                style={{ border: '2px solid #ff4d4f', fontWeight: 'bold' }}
              >
                统计看板
              </Button>
              <Button 
                icon={<UnorderedListOutlined />}
                onClick={() => navigate('/knowledge-lottery/sessions')}
                style={{ border: '2px solid #ff4d4f', fontWeight: 'bold' }}
              >
                会议列表
              </Button>
              {hasManagementAccess && (
                <Button 
                  icon={<SettingOutlined />}
                  onClick={() => navigate('/knowledge-lottery/manage')}
                  style={{ border: '2px solid #ff4d4f', fontWeight: 'bold' }}
                >
                  管理后台
                </Button>
              )}
              {isAdmin && (
                <Button 
                  icon={<LineChartOutlined />}
                  onClick={() => navigate('/statistics/user-activity')}
                  style={{ border: '2px solid #ff4d4f', fontWeight: 'bold' }}
                >
                  用户统计
                </Button>
              )}
              {currentUser && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  marginLeft: '16px',
                  paddingLeft: '16px',
                  borderLeft: '1px solid #e8e8e8'
                }}>
                  <UserOutlined style={{ color: '#666' }} />
                  <span style={{ color: '#666', fontSize: '14px' }}>
                    {currentUser.name || currentUser.email}
                  </span>
                  <Popconfirm
                    title="确定要退出登录吗？"
                    onConfirm={handleLogout}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button 
                      icon={<LogoutOutlined />}
                      danger
                      size="small"
                      style={{ marginLeft: '8px' }}
                    >
                      退出
                    </Button>
                  </Popconfirm>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '48px 24px' }}>
        {/* 待学习知识点 */}
        {latestContent && (
          <section style={{ marginBottom: '64px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                待学习知识点
              </h2>
              {formatSessionInfo(latestContent) && (
                <div style={{ 
                  border: '2px solid #ff4d4f', 
                  padding: '8px 24px',
                  background: '#fff'
                }}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#ff4d4f' }}>
                    {formatSessionInfo(latestContent)}
                  </span>
                </div>
              )}
            </div>
            
            <Card 
              style={{ 
                border: '2px solid #ff4d4f',
                borderRadius: '8px'
              }}
            >
              <Meta
                title={
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>
                      {latestContent.title}
                    </div>
                    <div style={{ fontSize: '12px', color: '#999', fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CalendarOutlined /> {formatDate(
                        latestContent.associatedSession?.sessionDate || 
                        latestContent.learnedDate || 
                        latestContent.createdAt
                      )}
                      {isLearned(latestContent) && (
                        <Tag color="success" style={{ margin: 0 }}>已学习</Tag>
                      )}
                    </div>
                  </div>
                }
                description={
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ 
                      whiteSpace: 'pre-wrap', 
                      lineHeight: '1.8',
                      fontSize: '14px',
                      maxHeight: '300px',
                      overflow: 'auto'
                    }}>
                      {latestContent.content}
                    </div>
                    <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
                      <Button 
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetail(
                          latestContent.id, 
                          latestContent._isPendingLearning, 
                          latestContent._sessionId || latestSessionId
                        )}
                        style={{ background: '#ff4d4f', borderColor: '#ff4d4f' }}
                      >
                        查看详情
                      </Button>
                    </div>
                  </div>
                }
              />
            </Card>
          </section>
        )}

        {/* 历史学习时间轴 */}
        {historyContents.length > 0 && (
          <section>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>
              历史学习
            </h2>
            <div style={{ maxWidth: '1000px', marginLeft: 0 }}>
              <Timeline
                mode="left"
                style={{ paddingLeft: '0' }}
                items={historyContents.map((content, index) => {
                  const date = content.learnedDate || content.createdAt;
                  return {
                    color: '#ff4d4f',
                    children: (
                      <div style={{ marginLeft: '20px', marginBottom: '32px' }}>
                        <Card
                          hoverable
                          onClick={() => handleViewDetail(content.id)}
                          style={{
                            border: '2px solid #d9d9d9',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.3s',
                            maxWidth: '700px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#ff4d4f';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 77, 79, 0.15)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#d9d9d9';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                                <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>
                                  {content.title}
                                </h3>
                                {formatSessionInfo(content) && (
                                  <Tag color="red" style={{ border: '2px solid #ff4d4f' }}>
                                    {formatSessionInfo(content)}
                                  </Tag>
                                )}
                              </div>
                              <div style={{ fontSize: '12px', color: '#999', fontFamily: 'monospace', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <CalendarOutlined /> {formatDate(
                                  content.associatedSession?.sessionDate || 
                                  content.learnedDate || 
                                  content.createdAt
                                )}
                                {isLearned(content) && (
                                  <Tag color="success" style={{ margin: 0 }}>已学习</Tag>
                                )}
                              </div>
                              <div style={{ 
                                fontSize: '14px', 
                                color: '#666',
                                lineHeight: '1.6',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}>
                                {content.content}
                              </div>
                            </div>
                            <Button 
                              type="link"
                              icon={<EyeOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetail(content.id);
                              }}
                            >
                              查看详情
                            </Button>
                          </div>
                        </Card>
                      </div>
                    ),
                    label: (
                      <div style={{ 
                        minWidth: '100px', 
                        textAlign: 'right',
                        paddingRight: '16px',
                        fontSize: '14px',
                        color: '#666',
                        fontFamily: 'monospace'
                      }}>
                        {formatDate(date)}
                      </div>
                    )
                  };
                })}
              />
            </div>
          </section>
        )}

        {/* 空状态 */}
        {!latestContent && historyContents.length === 0 && !loading && (
          <Empty
            description="暂无学习内容"
            style={{ marginTop: '100px' }}
          >
            {hasManagementAccess && (
              <Button 
                type="primary"
                onClick={() => navigate('/knowledge-lottery/manage')}
              >
                前往管理后台添加内容
              </Button>
            )}
          </Empty>
        )}
      </main>
    </div>
  );
};

export default KnowledgeLotteryHome;
